import React, { useState, useEffect } from 'react';
import { API } from '@/api';
import { SITE_ID } from '@/assets/meta/meta';
import { Star, Instagram, Globe, Youtube, PenLine, ExternalLink, ChevronLeft } from 'lucide-react';
import { Skeleton } from '@/Components/ui/skeleton';
import { Button } from '@/Components/ui/button';
import { Modal } from '@/Components/Common/Tailwind';
import { cn } from '@/Components/ui/utils';
import type { RecommendedReview, RecommendedReviewPlatform } from '@/api/type';
import { SectionHeader } from './SectionHeader';

const PLATFORM_CONFIG: Record<
    RecommendedReviewPlatform,
    { icon: React.ReactNode; label: string; color: string; bg: string }
> = {
    instagram: { icon: <Instagram size={12} />, label: 'Instagram', color: 'text-pink-500', bg: 'bg-pink-50' },
    naver_blog: { icon: <PenLine size={12} />, label: 'Naver Blog', color: 'text-green-600', bg: 'bg-green-50' },
    youtube: { icon: <Youtube size={12} />, label: 'YouTube', color: 'text-red-500', bg: 'bg-red-50' },
    web: { icon: <Globe size={12} />, label: 'Web', color: 'text-blue-500', bg: 'bg-blue-50' },
    other: { icon: <Globe size={12} />, label: 'Review', color: 'text-gray-500', bg: 'bg-gray-50' },
};

interface ExternalReviewCardProps {
    review: RecommendedReview;
    onClick: () => void;
    index?: number;
}

function ExternalReviewCard({ review, onClick, index = 0 }: ExternalReviewCardProps) {
    const config = PLATFORM_CONFIG[review.platform];

    return (
        <div
            onClick={onClick}
            style={{ animationDelay: `${index * 80}ms` }}
            className="animate-fade-up block bg-white rounded-2xl overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_16px_-4px_rgba(0,0,0,0.08)] hover:shadow-[0_2px_4px_rgba(0,0,0,0.05),0_12px_28px_-6px_rgba(0,0,0,0.12)] transition-[box-shadow,transform] duration-200 active:scale-[0.96] cursor-pointer"
        >
            <div className="flex h-28">
                {/* Thumbnail on the left */}
                <div className="w-28 h-28 shrink-0 bg-gray-100">
                    {review.thumbnail_url ? (
                        <img
                            src={review.thumbnail_url}
                            alt={review.title || 'Review'}
                            className="w-full h-full object-cover outline outline-1 -outline-offset-1 outline-black/10"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                            {config.icon}
                        </div>
                    )}
                </div>

                {/* Content on the right */}
                <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
                    <div className="space-y-1">
                        <div className="flex items-center justify-between">
                            <div className={cn("inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold", config.color, config.bg)}>
                                {config.icon}
                                {config.label}
                            </div>
                            {review.rating !== undefined && review.rating !== null && (
                                <div className="flex items-center gap-0.5 text-amber-500 text-[10px] font-bold tabular-nums">
                                    <Star size={10} fill="currentColor" />
                                    {Number(review.rating).toFixed(1)}
                                </div>
                            )}
                        </div>

                        <h3 className="font-bold text-[13px] text-gray-900 line-clamp-1">
                            {review.title || (review.content ? review.content.substring(0, 30) : '')}
                        </h3>

                        <p className="text-[11px] text-gray-500 line-clamp-2 leading-relaxed">
                            {review.content}
                        </p>
                    </div>

                    <div className="flex items-center justify-between mt-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                            {review.author_profile_image && (
                                <img src={review.author_profile_image} className="w-4 h-4 rounded-full outline outline-1 -outline-offset-1 outline-black/10" alt="" />
                            )}
                            <span className="text-[10px] font-bold text-gray-400 truncate">{review.author_name || 'Username'}</span>
                        </div>
                        {review.review_date && (
                            <span className="text-[9px] text-gray-300 shrink-0">
                                {new Date(review.review_date).toLocaleDateString('ko-KR')}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

interface RecommendedReviewSectionProps {
    siteId: number;
    enabled: boolean;
    title?: string | null;
}

export default function RecommendedReviewSection({ siteId, enabled, title }: RecommendedReviewSectionProps) {
    const [reviews, setReviews] = useState<RecommendedReview[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedReview, setSelectedReview] = useState<RecommendedReview | null>(null);
    const selectedPlatformConfig = selectedReview
        ? PLATFORM_CONFIG[selectedReview.platform]
        : null;

    useEffect(() => {
        if (!enabled) return;

        setIsLoading(true);
        API.get(`/sites/recommended-reviews/?site=${siteId}`)
            .then(res => {
                const data = res.data;
                setReviews(Array.isArray(data) ? data : (data?.results || []));
            })
            .catch(console.error)
            .finally(() => setIsLoading(false));
    }, [siteId, enabled]);

    if (!enabled || (!isLoading && !reviews.length)) return null;

    return (
        <section className="p-6 pb-8">
            <SectionHeader title={title || '우수 리뷰 추천'} />

            <div className="space-y-4">
                {isLoading ? (
                    [1, 2, 3].map(i => (
                        <div key={i} className="flex h-28 bg-white rounded-2xl shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_16px_-4px_rgba(0,0,0,0.08)] overflow-hidden">
                            <Skeleton className="w-28 h-28 shrink-0 rounded-none" />
                            <div className="flex-1 p-3 space-y-2">
                                <Skeleton className="h-4 w-1/3 rounded-full" />
                                <Skeleton className="h-4 w-full" />
                                <Skeleton className="h-8 w-full" />
                            </div>
                        </div>
                    ))
                ) : (
                    reviews.map((review: RecommendedReview, i: number) => (
                        <ExternalReviewCard
                            key={review.id}
                            review={review}
                            index={i}
                            onClick={() => setSelectedReview(review)}
                        />
                    ))
                )}
            </div>

            <Modal isOpen={!!selectedReview} toggle={() => setSelectedReview(null)} variant="full" className="gap-0 border-0 bg-white">
                    {selectedReview && (
                        selectedPlatformConfig && (
                        <div className="flex min-h-full flex-col">
                            <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
                                <button
                                    type="button"
                                    className="flex items-center justify-center w-10 h-10 -ml-1 rounded-full text-gray-700 hover:bg-gray-100 transition-[background-color,transform] active:scale-[0.96]"
                                    aria-label="Back"
                                    onClick={() => setSelectedReview(null)}
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                <h3 className="text-base font-semibold text-gray-900">리뷰 상세</h3>
                            </div>
                            {/* Header Image */}
                            <div className="relative aspect-video bg-gray-100">
                                {selectedReview.thumbnail_url ? (
                                    <img
                                        src={selectedReview.thumbnail_url}
                                        alt=""
                                        className="w-full h-full object-cover outline outline-1 -outline-offset-1 outline-black/10"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-300 bg-gray-50">
                                        <Globe size={48} className="opacity-20" />
                                    </div>
                                )}
                                <div className="absolute top-3 left-3">
                                    <span className={cn(
                                        "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold bg-white/95 backdrop-blur-sm shadow-sm",
                                        selectedPlatformConfig.color
                                    )}>
                                        {selectedPlatformConfig.icon}
                                        {selectedPlatformConfig.label}
                                    </span>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="flex-1 p-6 overflow-y-auto">
                                <div className="flex items-center gap-2.5 mb-4">
                                    {selectedReview.author_profile_image ? (
                                        <img src={selectedReview.author_profile_image} className="w-8 h-8 rounded-full object-cover outline outline-1 -outline-offset-1 outline-black/10" alt="" />
                                    ) : (
                                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                                            <Globe size={14} className="text-gray-400" />
                                        </div>
                                    )}
                                    <div className="flex flex-col">
                                        <span className="text-xs font-bold text-gray-900">{selectedReview.author_name || 'Anonymous'}</span>
                                        {selectedReview.review_date && (
                                            <span className="text-[10px] text-gray-400">
                                                {new Date(selectedReview.review_date).toLocaleDateString('ko-KR')}
                                            </span>
                                        )}
                                    </div>
                                    {selectedReview.rating && (
                                        <div className="flex items-center gap-1 text-amber-500 text-sm font-black ml-auto bg-amber-50 px-2 py-1 rounded-lg tabular-nums">
                                            <Star size={12} fill="currentColor" />
                                            {Number(selectedReview.rating).toFixed(1)}
                                        </div>
                                    )}
                                </div>

                                <h3 className="font-bold text-lg text-gray-900 mb-3 leading-snug text-balance">
                                    {selectedReview.title}
                                </h3>

                                <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap text-pretty">
                                    {selectedReview.content}
                                </p>
                            </div>

                            {/* Footer Action */}
                            <div className="p-5 border-t border-gray-100 bg-gray-50/50 mt-auto">
                                <Button
                                    variant="secondary" className='w-full'
                                    onClick={() => window.open(selectedReview.source_url, '_blank')}
                                >
                                    리뷰 보러가기 <ExternalLink size={18} className="ml-2" />
                                </Button>
                            </div>
                        </div>
                        )
                    )}
            </Modal>
        </section>
    );
}
